import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Layout, Table, Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, TimePicker, Space, Descriptions, Timeline,
  Typography, Divider, Alert, Spin, Card, Tooltip, App,
  Row, Col, Statistic, Empty,
} from 'antd';
import {
  PlusOutlined, CheckOutlined, CloseOutlined, PlayCircleOutlined,
  StopOutlined, FilePdfOutlined, EyeOutlined, DeleteOutlined,
  ReloadOutlined, EditOutlined, SearchOutlined,
  CalendarTwoTone, CarryOutTwoTone, CheckCircleTwoTone, ClockCircleTwoTone, CloseCircleTwoTone,
  ContactsTwoTone, FlagTwoTone, IdcardTwoTone, PieChartTwoTone, ProfileTwoTone,
  SafetyCertificateTwoTone, ScheduleTwoTone,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import NavigationBar from '../../components/NavigationBar';
import overtimeService from '../../services/overtimeService';
import OvertimeScheduleTab, { STATUS_TONE } from '../../components/OvertimeScheduleTab';
import DOTheme from '../../components/directOrders/doTheme';
import { STATUS_LABELS, statusTag } from '../../utils/overtimeStatus';
import '../../styles/overtime.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DAY_TYPE_LABELS = {
  weekday:        'Weekday (×1.0)',
  saturday:       'Saturday (×1.5)',
  sunday:         'Sunday (×2.0)',
  public_holiday: 'Public Holiday (×3.0)',
};

const fmt    = (v) => (v !== undefined && v !== null ? v : '—');
const fmtHrs = (h) => (h != null ? `${parseFloat(h).toFixed(2)} hrs` : '—');
const fmtEur = (v) => (v != null ? `€${parseFloat(v).toFixed(2)}` : '—');
const minLabel = (mins) => dayjs().startOf('day').add(mins, 'minute').format('HH:mm');
const fmtRange = (from, to) => {
  if (!from) return '—';
  const a = dayjs(from.slice(0, 10));
  const b = to ? dayjs(to.slice(0, 10)) : a;
  if (a.isSame(b, 'day')) return a.format('D MMM YYYY');
  return a.isSame(b, 'year') ? `${a.format('D MMM')} to ${b.format('D MMM YYYY')}` : `${a.format('D MMM YYYY')} to ${b.format('D MMM YYYY')}`;
};

/* The four steps every request goes through, in order. */
const STEP_DEFS = [
  { key: 'pending_coordinator_approval', name: 'Manager approval', tone: 'warn',   icon: <SafetyCertificateTwoTone twoToneColor="#b54708" /> },
  { key: 'pending_employee_action',      name: 'Clock in and out', tone: 'accent', icon: <ClockCircleTwoTone twoToneColor="#2748d9" /> },
  { key: 'pending_fm_approval',          name: 'Final approval',   tone: 'violet', icon: <CarryOutTwoTone twoToneColor="#6941c6" /> },
  { key: 'completed',                    name: 'Completed',        tone: 'ok',     icon: <CheckCircleTwoTone twoToneColor="#12805c" /> },
];
const STEP_ICON = {
  ...Object.fromEntries(STEP_DEFS.map((s) => [s.key, s.icon])),
  rejected: <CloseCircleTwoTone twoToneColor="#c4321f" />,
};

const IconTile = ({ icon, tone = 'muted' }) => (
  <span className={`ovt-ico tone-${tone}`} aria-hidden="true">{icon}</span>
);

const StatusWord = ({ status }) => (
  <span className={`ovt-status tone-${STATUS_TONE[status] || 'muted'}`}>{STATUS_LABELS[status] || status}</span>
);

/* Four dots: manager, employee, final approval, done. A rejection stops at the manager. */
const StepTrack = ({ index, rejected }) => (
  <span className="ovt-track" aria-hidden="true">
    {STEP_DEFS.map((s, i) => {
      let state = '';
      if (rejected) state = i === 0 ? 'is-bad' : '';
      else if (index === STEP_DEFS.length - 1 || i < index) state = 'is-done';
      else if (i === index) state = 'is-now';
      return (
        <React.Fragment key={s.key}>
          {i > 0 && <i className={!rejected && (index === STEP_DEFS.length - 1 || i <= index) ? 'is-done' : ''} />}
          <b className={state} />
        </React.Fragment>
      );
    })}
  </span>
);

/* Live time on shift, from the employee's clock-in. */
const ShiftTimer = ({ start, compact = false }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.floor((now - dayjs(start).valueOf()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return (
    <span className={`ovt-timer${compact ? ' is-compact' : ''}`} role="timer" aria-label={`On shift for ${h} hours ${m} minutes`}>
      <span className="ovt-timer__value num">{h}:{m}:{s}</span>
      {!compact && <span className="ovt-timer__label">on shift since {dayjs(start).format('HH:mm')}</span>}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Overtime = () => {
  const { user } = useAuth();
  const { modal, message } = App.useApp();

  /* ── Role flags ──────────────────────────────────────────── */
  const roleId     = parseInt(user?.role ?? user?.role_id, 10);
  const additionalRoles = (user?.additionalRoles || []).map(r => parseInt(r, 10));
  const allRoleIds = [roleId, ...additionalRoles].filter(r => !isNaN(r));
  const isAdmin    = allRoleIds.some(r => [3, 4].includes(r));   // Admin, Employee Admin (backend PEOPLE_MANAGERS)
  const isFM       = allRoleIds.includes(5);
  const isSO       = allRoleIds.includes(7);   // Senior Officer
  const isFMorSO   = isFM || isSO;
  const [isCoordinator, setIsCoordinator] = useState(false);

  /* ── Shared data ─────────────────────────────────────────── */
  const [festivals, setFestivals] = useState([]);
  const [employees, setEmployees] = useState([]);

  /* ── Request lists ───────────────────────────────────────── */
  const [allRequests,  setAllRequests]  = useState([]);
  const [myRequests,   setMyRequests]   = useState([]);
  const [loading,      setLoading]      = useState(false);

  /* ── Page view and filters ───────────────────────────────── */
  const [view,           setView]           = useState('schedule');
  const [stepFilter,     setStepFilter]     = useState('all');
  const [myFilter,       setMyFilter]       = useState('all');
  const [festivalFilter, setFestivalFilter] = useState('all');
  const [search,         setSearch]         = useState('');

  /* ── Coordinators & public holidays (admin) ──────────────── */
  const [coordinators,   setCoordinators]   = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);

  /* ── Reports ─────────────────────────────────────────────── */
  const [reportFestivalId, setReportFestivalId] = useState(null);
  const [reportEmployeeId, setReportEmployeeId] = useState(null);
  const [reportData,       setReportData]       = useState(null);
  const [reportLoading,    setReportLoading]    = useState(false);

  /* ── Detail modal ────────────────────────────────────────── */
  const [detailRec,     setDetailRec]     = useState(null);
  const [detailOpen,    setDetailOpen]    = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  /* ── Create request modal ────────────────────────────────── */
  const [createOpen,    setCreateOpen]    = useState(false);
  const [createForm]                      = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);

  /* ── Coordinator decision modal ─────────────────────────── */
  const [coordDecOpen,    setCoordDecOpen]    = useState(false);
  const [coordDecRec,     setCoordDecRec]     = useState(null);
  const [coordDecForm]                        = Form.useForm();
  const [coordDecLoading, setCoordDecLoading] = useState(false);

  /* ── FM Final Approval modal ─────────────────────────────── */
  const [fmApprovalOpen,    setFmApprovalOpen]    = useState(false);
  const [fmApprovalRec,     setFmApprovalRec]     = useState(null);
  const [fmApprovalForm]                          = Form.useForm();
  const [fmApprovalLoading, setFmApprovalLoading] = useState(false);

  /* ── Amend modal (FM/SO to edit rejected requests) ────────── */
  const [amendOpen,    setAmendOpen]    = useState(false);
  const [amendRec,     setAmendRec]     = useState(null);
  const [amendForm]                     = Form.useForm();
  const [amendLoading, setAmendLoading] = useState(false);

  /* ── Coordinator management modal (admin) ────────────────── */
  const [addCoordOpen,    setAddCoordOpen]    = useState(false);
  const [addCoordForm]                        = Form.useForm();
  const [addCoordLoading, setAddCoordLoading] = useState(false);

  /* ── Public holiday modal (admin) ────────────────────────── */
  const [phOpen,    setPhOpen]    = useState(false);
  const [phRec,     setPhRec]     = useState(null);
  const [phForm]                  = Form.useForm();
  const [phLoading, setPhLoading] = useState(false);

  /* ═══════════════════════════════════════════════════════════
     DATA FETCHING
  ═══════════════════════════════════════════════════════════ */
  const loadLookups = useCallback(async () => {
    try {
      const [f, e] = await Promise.all([
        overtimeService.getFestivals().catch(() => []),
        overtimeService.getEmployees(),
      ]);
      setFestivals(Array.isArray(f) ? f : f.festivals || []);
      const raw = Array.isArray(e) ? e : e.employees || [];
      setEmployees(raw);
    } catch { /* non-fatal */ }
  }, []);

  const checkCoordinator = useCallback(async () => {
    try {
      const coords = await overtimeService.getCoordinators();
      const list = Array.isArray(coords) ? coords : coords.coordinators || [];
      const myEmail = user?.email?.toLowerCase();
      const isCoord = myEmail && list.some(c => c.email?.toLowerCase() === myEmail);
      setIsCoordinator(isCoord);
    } catch { setIsCoordinator(false); }
  }, [user]);

  const loadAllRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await overtimeService.getRequests();
      const requests = Array.isArray(data) ? data : data.requests || [];
      setAllRequests(requests);
    } catch (err) { message.error(err.message); }
    finally { setLoading(false); }
  }, []);

  const loadMyRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await overtimeService.getMyRequests();
      const requests = Array.isArray(data) ? data : data.requests || [];
      setMyRequests(requests);
    } catch (err) { message.error(err.message); }
    finally { setLoading(false); }
  }, []);

  const loadCoordinators = useCallback(async () => {
    try {
      const data = await overtimeService.getCoordinators();
      setCoordinators(Array.isArray(data) ? data : data.coordinators || []);
    } catch (err) { message.error(err.message); }
  }, []);

  const loadPublicHolidays = useCallback(async () => {
    try {
      const data = await overtimeService.getPublicHolidays();
      setPublicHolidays(Array.isArray(data) ? data : data.holidays || []);
    } catch (err) { message.error(err.message); }
  }, []);

  useEffect(() => {
    loadLookups();
    checkCoordinator();
    loadAllRequests();
    loadMyRequests();
    loadPublicHolidays();
    if (isAdmin) loadCoordinators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ═══════════════════════════════════════════════════════════
     DETAIL VIEW
  ═══════════════════════════════════════════════════════════ */
  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await overtimeService.getById(id);
      const raw = data.request ?? data;
      const rec = {
        ...raw,
        employee_name:     raw.employee_name
                           || `${raw.employee_first_name || ''} ${raw.employee_last_name || ''}`.trim()
                           || '—',
        actual_start_time: raw.actual_start_time ?? raw.employee_start_timestamp,
        actual_end_time:   raw.actual_end_time   ?? raw.employee_end_timestamp,
        net_hours:         raw.net_hours         ?? raw.net_overtime_hours,
        hourly_rate:       raw.hourly_rate       ?? raw.employee_hourly_rate,
        history: (data.history ?? []).map(h => ({
          ...h,
          action: h.action ?? h.action_type,
        })),
      };
      setDetailRec(rec);
    } catch (err) { message.error(err.message); setDetailOpen(false); }
    finally { setDetailLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════
     EMPLOYEE TRACKING ACTIONS
  ═══════════════════════════════════════════════════════════ */
  const employeeAction = async (action, id) => {
    try {
      if      (action === 'start')   await overtimeService.employeeStart(id);
      else if (action === 'end')     await overtimeService.employeeEnd(id);
      else if (action === 'confirm') await overtimeService.employeeConfirm(id);
      await loadMyRequests();
      loadAllRequests();
      message.success('Updated successfully');
    } catch (err) { 
      message.error(err.message); 
    }
  };

  /* ═══════════════════════════════════════════════════════════
     DELETE
  ═══════════════════════════════════════════════════════════ */
  const handleDelete = (id) => {
    modal.confirm({
      title: 'Delete this overtime request?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await overtimeService.deleteRequest(id);
          message.success('Deleted');
          loadAllRequests();
        } catch (err) { message.error(err.message); }
      },
    });
  };

  /* ═══════════════════════════════════════════════════════════
     DOWNLOAD PDF
  ═══════════════════════════════════════════════════════════ */
  const handleDownloadPdf = async (id, requestNumber) => {
    try {
      message.loading({ content: 'Generating PDF...', key: 'pdf' });
      const blob = await overtimeService.downloadPdf(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Overtime_${requestNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success({ content: 'PDF downloaded', key: 'pdf' });
    } catch (err) {
      message.error({ content: err.message, key: 'pdf' });
    }
  };

  /* ═══════════════════════════════════════════════════════════
     CREATE REQUEST
  ═══════════════════════════════════════════════════════════ */
  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      const ids = Array.isArray(values.employee_ids) ? values.employee_ids : [values.employee_ids];
      const base = {
        festival_id:          values.festival_id,
        festival_date_from:   values.festival_date_from?.format('YYYY-MM-DD'),
        festival_date_to:     values.festival_date_to?.format('YYYY-MM-DD'),
        estimated_time_from:  values.estimated_time_from?.format('HH:mm'),
        estimated_time_to:    values.estimated_time_to?.format('HH:mm'),
        estimated_task:       values.estimated_task,
      };
      await Promise.all(ids.map(id => overtimeService.createRequest({ ...base, employee_id: id })));
      message.success(ids.length > 1 ? `${ids.length} overtime requests created` : 'Overtime request created');
      setCreateOpen(false);
      createForm.resetFields();
      loadAllRequests();
      loadMyRequests();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message);
    } finally { setCreateLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════
     COORDINATOR DECISION
  ═══════════════════════════════════════════════════════════ */
  const openCoordDecision = (rec) => {
    setCoordDecRec(rec);
    setCoordDecOpen(true);
    coordDecForm.resetFields();
  };

  const handleCoordDecision = async (decision) => {
    try {
      const values = await coordDecForm.validateFields();
      setCoordDecLoading(true);
      await overtimeService.coordinatorDecision(coordDecRec.id, { decision, notes: values.notes });
      message.success(`Request ${decision}`);
      setCoordDecOpen(false);
      loadAllRequests();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message);
    } finally { setCoordDecLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════
     FM FINAL APPROVAL
  ═══════════════════════════════════════════════════════════ */
  const openFmApproval = async (rec) => {
    setFmApprovalRec(rec);
    setFmApprovalOpen(true);
    
    // Fetch full record details to get employee tracked times
    try {
      const data = await overtimeService.getById(rec.id);
      const fullRec = data.request ?? data;
      
      // Pre-fill form with employee tracked times or estimated values
      const startTime = fullRec.employee_start_timestamp 
        ? dayjs(fullRec.employee_start_timestamp) 
        : fullRec.estimated_time_from ? dayjs(`2000-01-01 ${fullRec.estimated_time_from}`) : null;
      
      const endTime = fullRec.employee_end_timestamp 
        ? dayjs(fullRec.employee_end_timestamp) 
        : fullRec.estimated_time_to ? dayjs(`2000-01-01 ${fullRec.estimated_time_to}`) : null;
      
      // Calculate actual hours if employee tracked times exist
      let calculatedHours = fullRec.estimated_hours || null;
      if (fullRec.employee_start_timestamp && fullRec.employee_end_timestamp) {
        const start = dayjs(fullRec.employee_start_timestamp);
        const end = dayjs(fullRec.employee_end_timestamp);
        const diffMinutes = end.diff(start, 'minute');
        const hours = diffMinutes / 60;
        // Round to nearest 0.5, but ensure at least 0.5 if there's any tracked time
        calculatedHours = hours > 0 ? Math.max(0.5, Math.round(hours * 2) / 2) : 0.5;
      }
      
      fmApprovalForm.setFieldsValue({
        final_date: fullRec.festival_date_from ? dayjs(fullRec.festival_date_from) : null,
        final_time_in: startTime,
        final_time_out: endTime,
        final_hours: calculatedHours,
        final_task: fullRec.estimated_task || '',
      });
    } catch (err) {
      fmApprovalForm.resetFields();
    }
  };

  const handleFmApproval = async () => {
    try {
      const values = await fmApprovalForm.validateFields();
      setFmApprovalLoading(true);
      const payload = {
        ...values,
        final_date:     values.final_date?.format('YYYY-MM-DD'),
        final_time_in:  values.final_time_in?.format('HH:mm'),
        final_time_out: values.final_time_out?.format('HH:mm'),
      };
      await overtimeService.fmFinalApproval(fmApprovalRec.id, payload);
      message.success('Final approval submitted');
      setFmApprovalOpen(false);
      loadAllRequests();
      loadMyRequests();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Failed to submit approval');
    } finally { setFmApprovalLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════
     AMEND REJECTED REQUEST (FM/SO)
  ═══════════════════════════════════════════════════════════ */
  const openAmend = async (rec) => {
    setAmendOpen(true);
    setAmendLoading(true);
    try {
      const data = await overtimeService.getById(rec.id);
      const full = data.request ?? data;
      setAmendRec(full);
      amendForm.setFieldsValue({
        employee_id:         full.employee_id,
        festival_id:         full.festival_id,
        festival_date_from:  full.festival_date_from ? dayjs(full.festival_date_from) : null,
        festival_date_to:    full.festival_date_to ? dayjs(full.festival_date_to) : null,
        estimated_time_from: full.estimated_time_from ? dayjs(`2000-01-01 ${full.estimated_time_from}`) : null,
        estimated_time_to:   full.estimated_time_to ? dayjs(`2000-01-01 ${full.estimated_time_to}`) : null,
        estimated_task:      full.estimated_task,
      });
    } catch (err) {
      message.error(err.message);
      setAmendOpen(false);
    } finally {
      setAmendLoading(false);
    }
  };

  const handleAmend = async () => {
    try {
      const values = await amendForm.validateFields();
      setAmendLoading(true);
      const payload = {
        employee_id:         values.employee_id,
        festival_id:         values.festival_id,
        festival_date_from:  values.festival_date_from?.format('YYYY-MM-DD'),
        festival_date_to:    values.festival_date_to?.format('YYYY-MM-DD'),
        estimated_time_from: values.estimated_time_from?.format('HH:mm'),
        estimated_time_to:   values.estimated_time_to?.format('HH:mm'),
        estimated_task:      values.estimated_task,
      };
      await overtimeService.updateRequest(amendRec.id, payload);
      message.success('Request amended and resubmitted for approval');
      setAmendOpen(false);
      loadAllRequests();
      loadMyRequests();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message);
    } finally { setAmendLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════
     ADD COORDINATOR (admin)
  ═══════════════════════════════════════════════════════════ */
  const handleAddCoord = async () => {
    try {
      const values = await addCoordForm.validateFields();
      setAddCoordLoading(true);
      const ids = Array.isArray(values.employee_ids) ? values.employee_ids : [values.employee_ids];
      await Promise.all(ids.map(id => overtimeService.addCoordinator({ employee_id: id, festival_id: values.festival_id })));
      message.success(ids.length > 1 ? `${ids.length} managers added` : 'Manager added');
      setAddCoordOpen(false);
      addCoordForm.resetFields();
      loadCoordinators();
      checkCoordinator();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message);
    } finally { setAddCoordLoading(false); }
  };

  const handleRemoveCoord = (id) => {
    modal.confirm({
      title: 'Remove this coordinator?',
      okType: 'danger',
      onOk: async () => {
        try {
          await overtimeService.removeCoordinator(id);
          message.success('Removed');
          loadCoordinators();
          checkCoordinator();
        } catch (err) { message.error(err.message); }
      },
    });
  };

  /* ═══════════════════════════════════════════════════════════
     PUBLIC HOLIDAYS (admin)
  ═══════════════════════════════════════════════════════════ */
  const openPh = (rec = null) => {
    setPhRec(rec);
    setPhOpen(true);
    if (rec) phForm.setFieldsValue({ name: rec.holiday_name, date: dayjs(rec.holiday_date) });
    else phForm.resetFields();
  };

  const handlePhSave = async () => {
    try {
      const values = await phForm.validateFields();
      setPhLoading(true);
      const payload = { holiday_name: values.name, holiday_date: values.date?.format('YYYY-MM-DD') };
      if (phRec) await overtimeService.updatePublicHoliday(phRec.id, payload);
      else       await overtimeService.addPublicHoliday(payload);
      message.success(phRec ? 'Updated' : 'Added');
      setPhOpen(false);
      loadPublicHolidays();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message);
    } finally { setPhLoading(false); }
  };

  const handlePhDelete = (id) => {
    modal.confirm({
      title: 'Delete this public holiday?',
      okType: 'danger',
      onOk: async () => {
        try {
          await overtimeService.deletePublicHoliday(id);
          message.success('Deleted');
          loadPublicHolidays();
        } catch (err) { message.error(err.message); }
      },
    });
  };

  /* ═══════════════════════════════════════════════════════════
     REPORTS
  ═══════════════════════════════════════════════════════════ */
  const fetchReport = async (type) => {
    setReportLoading(true);
    setReportData(null);
    try {
      let data;
      if (type === 'festival') data = await overtimeService.reportByFestival(reportFestivalId);
      else                     data = await overtimeService.reportByEmployee(reportEmployeeId);
      setReportData({ type, data });
    } catch (err) { message.error(err.message); }
    finally { setReportLoading(false); }
  };

  /* ── FM/SO row actions ─────────────────────────────────────── */
  const fmActions = (rec) => {
    if (rec.status === 'rejected') {
      return (
        <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => openAmend(rec)}>
          Amend
        </Button>
      );
    }
    if (rec.status !== 'pending_fm_approval') return null;
    const festivalTo = rec.festival_date_to ? dayjs(rec.festival_date_to) : null;
    const canApprove = festivalTo ? dayjs().isAfter(festivalTo) : false;
    return (
      <Tooltip title={canApprove ? 'Final Approval' : `Available after ${festivalTo?.format('DD/MM/YYYY') ?? ''}`}>
        <Button size="small" type="primary" disabled={!canApprove}
          icon={<CheckOutlined />} onClick={() => openFmApproval(rec)}>
          Approve
        </Button>
      </Tooltip>
    );
  };

  /* ── Coordinator row actions ────────────────────────────────── */
  const coordActions = (rec) => {
    if (rec.status !== 'pending_coordinator_approval') return null;
    return (
      <Button size="small" type="primary" onClick={() => openCoordDecision(rec)}>
        Decide
      </Button>
    );
  };

  /* ═══════════════════════════════════════════════════════════
     STEPS, VIEWS AND FILTERS
  ═══════════════════════════════════════════════════════════ */
  const canSeeRequests = isAdmin || isFMorSO || isCoordinator;
  const canSeeReports  = isAdmin || isFMorSO;
  const myEmail = (user?.email || '').toLowerCase();
  const myIds = useMemo(() => new Set(myRequests.map((r) => r.id)), [myRequests]);
  const isMine = (rec) => myIds.has(rec.id) || (rec.employee_email || '').toLowerCase() === myEmail;

  const statusCounts = useMemo(() => {
    const counts = {};
    // A coordinator's list only holds requests awaiting them, so add the viewer's own as well.
    const seen = new Set();
    [...allRequests, ...myRequests].forEach((r) => {
      if (seen.has(r.id)) return;
      seen.add(r.id);
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [allRequests, myRequests]);

  const myStepCounts = useMemo(() => {
    const counts = {};
    myRequests.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [myRequests]);

  // Plain employees see their own overtime in the steps; everyone else what the backend lets them see.
  const stepCounts = canSeeRequests ? statusCounts : myStepCounts;
  const youOn = {
    pending_coordinator_approval: isCoordinator,
    pending_employee_action: (myStepCounts.pending_employee_action || 0) > 0,
    pending_fm_approval: isFMorSO,
    completed: false,
  };

  const openStep = (status) => {
    const inList = allRequests.some((r) => r.status === status);
    const inMine = myRequests.some((r) => r.status === status);
    if (canSeeRequests && (inList || !inMine)) {
      setStepFilter(status);
      setView('requests');
    } else {
      setMyFilter(status);
      setView('my');
    }
  };

  const viewItems = [
    { key: 'schedule', label: 'Schedule', icon: <CalendarTwoTone twoToneColor="#2748d9" /> },
    ...(canSeeRequests ? [{ key: 'requests', label: 'Requests', icon: <ProfileTwoTone twoToneColor="#2748d9" /> }] : []),
    { key: 'my', label: 'My overtime', icon: <IdcardTwoTone twoToneColor="#2748d9" /> },
  ];
  const adminItems = [
    ...(isAdmin ? [
      { key: 'coords', label: 'Managers', icon: <ContactsTwoTone twoToneColor="#344054" /> },
      { key: 'holidays', label: 'Public holidays', icon: <FlagTwoTone twoToneColor="#344054" /> },
    ] : []),
    ...(canSeeReports ? [{ key: 'reports', label: 'Reports', icon: <PieChartTwoTone twoToneColor="#344054" /> }] : []),
  ];

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRequests.filter((r) =>
      (stepFilter === 'all' || r.status === stepFilter)
      && (festivalFilter === 'all' || String(r.festival_id) === String(festivalFilter))
      && (!term || [r.request_number, r.employee_name, r.festival_name, r.estimated_task, r.creator_name]
        .filter(Boolean).join(' ').toLowerCase().includes(term))
    );
  }, [allRequests, stepFilter, festivalFilter, search]);

  const requestFestivals = useMemo(() => {
    const map = new Map();
    allRequests.forEach((r) => { if (r.festival_id) map.set(String(r.festival_id), r.festival_name || `Festival ${r.festival_id}`); });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [allRequests]);

  // Same role rules as before for the row actions on the request list.
  const listActions = (isAdmin || isFMorSO) && isCoordinator
    ? (rec) => coordActions(rec) || fmActions(rec)
    : isFMorSO ? fmActions : isCoordinator ? coordActions : null;

  const stepOf = (status) => STEP_DEFS.findIndex((s) => s.key === status);

  /* ── One request as a ruled row ───────────────────────────── */
  const renderRequestRow = (rec) => (
    <li
      key={rec.id}
      className="ovt-req"
      tabIndex={0}
      aria-label={`Open ${rec.request_number || `request ${rec.id}`}`}
      onClick={() => openDetail(rec.id)}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openDetail(rec.id); } }}
    >
      <span className="ovt-req__ref num">
        {rec.request_number || `#${rec.id}`}
        <small>{fmtRange(rec.festival_date_from, rec.festival_date_to)}</small>
      </span>
      <span className="ovt-req__main">
        <span className="ovt-req__who">{rec.employee_name || '—'}</span>
        <span className="ovt-req__task">{[rec.festival_name, rec.estimated_task].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="ovt-req__time num">
        {rec.estimated_time_from ? `${rec.estimated_time_from.slice(0, 5)} to ${(rec.estimated_time_to || '').slice(0, 5)}` : '—'}
        <small>{fmtHrs(rec.final_hours ?? rec.estimated_hours)}</small>
      </span>
      <span className="ovt-req__state">
        <StatusWord status={rec.status} />
        <StepTrack index={stepOf(rec.status)} rejected={rec.status === 'rejected'} />
      </span>
      <span className="ovt-req__actions" onClick={(e) => e.stopPropagation()}>
        {rec.status === 'completed' && (
          <Tooltip title="Download PDF">
            <Button icon={<FilePdfOutlined />} onClick={() => handleDownloadPdf(rec.id, rec.request_number)} aria-label="Download PDF" />
          </Tooltip>
        )}
        {listActions && listActions(rec)}
        {isAdmin && (
          <Tooltip title="Delete">
            <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(rec.id)} aria-label="Delete request" />
          </Tooltip>
        )}
      </span>
    </li>
  );

  /* ── Employee clock controls for one of my requests ────────── */
  const clockButtons = (rec, size) => {
    if (rec.status !== 'pending_employee_action') return null;
    if (!rec.employee_start_timestamp) {
      return <Button type="primary" size={size} icon={<PlayCircleOutlined />} onClick={() => employeeAction('start', rec.id)}>Start work</Button>;
    }
    if (!rec.employee_end_timestamp) {
      return <Button type="primary" size={size} icon={<StopOutlined />} onClick={() => employeeAction('end', rec.id)}>End work</Button>;
    }
    return <Button type="primary" size={size} icon={<CheckOutlined />} onClick={() => employeeAction('confirm', rec.id)}>Confirm hours</Button>;
  };

  const myActive = myRequests.find((r) => r.status === 'pending_employee_action' && r.employee_start_timestamp && !r.employee_end_timestamp)
    || myRequests.find((r) => r.status === 'pending_employee_action');

  /* ── Schedule details bar (above the grid) ────────────────── */
  const renderPanel = (selected, { date, shiftCount, clear }) => {
    if (!selected) {
      if (myActive) {
        const live = myActive.employee_start_timestamp && !myActive.employee_end_timestamp;
        return (
          <div className="ovt-insp">
            <div className="ovt-insp__lead">
              <IconTile tone="accent" icon={<ClockCircleTwoTone twoToneColor="#2748d9" />} />
              <div className="ovt-insp__heading">
                <p className="ovt-panel__kicker">Your overtime</p>
                <h3 className="ovt-panel__title">{myActive.festival_name}</h3>
                <p className="ovt-panel__text">{myActive.estimated_task || 'Overtime'}</p>
              </div>
            </div>
            <div className="ovt-insp__end">
              {live && <ShiftTimer start={myActive.employee_start_timestamp} />}
              <div className="ovt-panel__actions">
                {clockButtons(myActive, 'large')}
                <Button size="large" onClick={() => openDetail(myActive.id)}>Details</Button>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="ovt-insp is-quiet">
          <div className="ovt-insp__lead">
            <IconTile tone="muted" icon={<ScheduleTwoTone twoToneColor="#667085" />} />
            <div className="ovt-insp__heading">
              <h3 className="ovt-panel__title">{date.format('dddd D MMMM')}</h3>
              <p className="ovt-panel__text">
                {shiftCount === 0 ? 'No overtime on this day.' : `${shiftCount} ${shiftCount === 1 ? 'shift' : 'shifts'}. Select one to see its details and actions.`}
              </p>
            </div>
          </div>
        </div>
      );
    }

    const rec = selected.req;
    const mine = isMine(rec);
    const roleAction = listActions ? listActions(rec) : null;
    const live = mine && rec.employee_start_timestamp && !rec.employee_end_timestamp && rec.status === 'pending_employee_action';
    const facts = [
      ['Task', rec.estimated_task || '—'],
      ['Shift', selected.start == null
        ? 'Time not recorded'
        : `${minLabel(selected.start)} to ${minLabel(selected.end % 1440)}${selected.isEstimate ? ' (est.)' : ''}`],
      ['Dates', fmtRange(rec.festival_date_from, rec.festival_date_to)],
      rec.employee_start_timestamp && ['Started', dayjs(rec.employee_start_timestamp).format('D MMM, HH:mm')],
      rec.employee_end_timestamp && ['Ended', dayjs(rec.employee_end_timestamp).format('D MMM, HH:mm')],
      ['Hours', fmtHrs(rec.final_hours ?? rec.estimated_hours)],
      rec.creator_name && ['Raised by', rec.creator_name],
    ].filter(Boolean);
    return (
      <div className="ovt-insp is-selected">
        <div className="ovt-insp__lead">
          <IconTile tone={STATUS_TONE[rec.status] || 'muted'} icon={STEP_ICON[rec.status] || <ClockCircleTwoTone />} />
          <div className="ovt-insp__heading">
            <p className="ovt-panel__kicker num">{[rec.request_number, rec.festival_name].filter(Boolean).join(' · ')}</p>
            <h3 className="ovt-panel__title">{mine ? 'Your shift' : rec.employee_name}</h3>
            <StatusWord status={rec.status} />
          </div>
        </div>
        <dl className="ovt-kv">
          {facts.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd className={k === 'Task' || k === 'Raised by' ? '' : 'num'}>{v}</dd></div>
          ))}
        </dl>
        <div className="ovt-insp__end">
          {live && <ShiftTimer start={rec.employee_start_timestamp} />}
          <div className="ovt-panel__actions">
            {mine && clockButtons(rec)}
            {roleAction}
            {rec.status === 'completed' && (
              <Button icon={<FilePdfOutlined />} onClick={() => handleDownloadPdf(rec.id, rec.request_number)}>PDF</Button>
            )}
            <Button onClick={() => openDetail(rec.id)}>Details</Button>
            <Tooltip title="Close">
              <Button type="text" icon={<CloseOutlined />} onClick={clear} aria-label="Close shift details" />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  /* ── Views ─────────────────────────────────────────────────── */
  const ScheduleView = (
    <OvertimeScheduleTab
      employees={employees}
      requests={allRequests}
      loading={loading}
      onRefresh={() => { loadAllRequests(); loadMyRequests(); }}
      publicHolidays={publicHolidays}
      renderPanel={renderPanel}
    />
  );

  const RequestsView = (
    <section className="ovt-card">
      <div className="ovt-toolbar">
        <div className="ovt-toolbar__lead">
          <h2 className="ovt-h2">
            {stepFilter === 'all' ? 'All requests' : stepFilter === 'rejected' ? 'Rejected' : `Step ${stepOf(stepFilter) + 1}: ${STEP_DEFS[stepOf(stepFilter)]?.name}`}
          </h2>
          <span className="ovt-muted num">
            {filteredRequests.length} {filteredRequests.length === 1 ? 'request' : 'requests'} · {fmtHrs(filteredRequests.reduce((s, r) => s + (parseFloat(r.final_hours ?? r.estimated_hours) || 0), 0))}
          </span>
          {stepFilter !== 'all' && (
            <Button type="link" onClick={() => setStepFilter('all')}>Show all steps</Button>
          )}
        </div>
        <div className="ovt-toolbar__tools">
          <Select
            value={festivalFilter}
            onChange={setFestivalFilter}
            style={{ width: 200 }}
            aria-label="Festival"
            options={[{ value: 'all', label: 'All festivals' }, ...requestFestivals.map(([id, name]) => ({ value: id, label: name }))]}
          />
          <Input
            allowClear
            prefix={<SearchOutlined aria-hidden="true" />}
            placeholder="Search requests"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
            aria-label="Search requests"
          />
          <Button icon={<ReloadOutlined />} onClick={loadAllRequests} aria-label="Refresh" />
        </div>
      </div>
      {loading && allRequests.length === 0 ? (
        <div className="ovt-empty"><Spin /></div>
      ) : filteredRequests.length === 0 ? (
        <div className="ovt-empty">
          <strong>{allRequests.length === 0 ? 'No overtime requests yet' : 'No requests match'}</strong>
          {allRequests.length === 0 ? 'Requests you can see will appear here.' : 'Try another step, festival or search.'}
        </div>
      ) : (
        <ul className="ovt-reqs">
          {filteredRequests.map(renderRequestRow)}
        </ul>
      )}
    </section>
  );

  const myList = myRequests.filter((r) => myFilter === 'all' || r.status === myFilter);
  const MyView = (
    <section className="ovt-card">
      <div className="ovt-toolbar">
        <div className="ovt-toolbar__lead">
          <h2 className="ovt-h2">My overtime</h2>
          <span className="ovt-muted num">{myRequests.length} {myRequests.length === 1 ? 'request' : 'requests'}</span>
          {myFilter !== 'all' && <Button type="link" onClick={() => setMyFilter('all')}>Show all</Button>}
        </div>
        <div className="ovt-toolbar__tools">
          <Button icon={<ReloadOutlined />} onClick={loadMyRequests} aria-label="Refresh" />
        </div>
      </div>
      {loading && myRequests.length === 0 ? (
        <div className="ovt-empty"><Spin /></div>
      ) : myList.length === 0 ? (
        <div className="ovt-empty">
          <strong>No overtime here</strong>
          When a manager approves overtime for you, it appears here so you can clock in and out.
        </div>
      ) : (
        <ul className="ovt-mine">
          {myList.map((req) => {
            const onShift = req.status === 'pending_employee_action' && req.employee_start_timestamp && !req.employee_end_timestamp;
            return (
              <li key={req.id} className={`ovt-mine__item${onShift ? ' is-live' : ''}`}>
                <IconTile
                  tone={STATUS_TONE[req.status] || 'muted'}
                  icon={STEP_ICON[req.status] || <ClockCircleTwoTone />}
                />
                <div className="ovt-mine__main">
                  <span className="ovt-mine__title">{req.festival_name || 'Overtime'}</span>
                  <span className="ovt-mine__meta num">
                    {[req.request_number, fmtRange(req.festival_date_from, req.festival_date_to),
                      req.estimated_time_from ? `${req.estimated_time_from.slice(0, 5)} to ${(req.estimated_time_to || '').slice(0, 5)}` : null,
                      fmtHrs(req.final_hours ?? req.estimated_hours)].filter(Boolean).join(' · ')}
                  </span>
                  {req.employee_start_timestamp && (
                    <span className="ovt-mine__clock num">
                      Started {dayjs(req.employee_start_timestamp).format('D MMM, HH:mm')}
                      {req.employee_end_timestamp && ` · Ended ${dayjs(req.employee_end_timestamp).format('HH:mm')}`}
                    </span>
                  )}
                </div>
                <div className="ovt-mine__side">
                  <StatusWord status={req.status} />
                  {onShift && <ShiftTimer start={req.employee_start_timestamp} compact />}
                </div>
                <div className="ovt-mine__actions">
                  {clockButtons(req, 'large')}
                  {req.status === 'completed' && (
                    <Button size="large" icon={<FilePdfOutlined />} onClick={() => handleDownloadPdf(req.id, req.request_number)}>PDF</Button>
                  )}
                  <Button size="large" onClick={() => openDetail(req.id)}>Details</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const ReportsContent = (
    <div>
      <Title level={5}>Overtime Reports</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card title="By Festival" size="small">
          <Space>
            <Select
              showSearch
              placeholder="Select festival"
              style={{ width: 240 }}
              onChange={(v) => setReportFestivalId(v)}
              optionFilterProp="children"
            >
              {festivals.map(f => <Option key={f.id} value={f.id}>{f.name}</Option>)}
            </Select>
            <Button type="primary" loading={reportLoading}
              onClick={() => fetchReport('festival')} disabled={!reportFestivalId}>
              Generate
            </Button>
          </Space>
        </Card>

        <Card title="By Employee" size="small">
          <Space>
            <Select
              showSearch
              placeholder="Select employee"
              style={{ width: 240 }}
              onChange={(v) => setReportEmployeeId(v)}
              optionFilterProp="children"
            >
              {employees.map(e => <Option key={e.id} value={e.id}>{e.name || e.full_name}</Option>)}
            </Select>
            <Button type="primary" loading={reportLoading}
              onClick={() => fetchReport('employee')} disabled={!reportEmployeeId}>
              Generate
            </Button>
          </Space>
        </Card>

        {reportData && (() => {
          const d = reportData.data || {};
          const overtimes = d.overtimes || [];
          const summary = d.summary || {};
          const subject = d.employee
            ? (`${d.employee.fName || ''} ${d.employee.lName || ''}`.trim() || d.employee.empEmail)
            : (d.festival?.name || d.festival?.festival_name);

          // Pay fields are stripped (not nulled) from the response for non-admin/
          // non-Employee-Admin viewers — check presence, don't assume the value.
          const canSeePay = summary.total_pay !== undefined
            || overtimes.some((o) => o.employee_hourly_rate !== undefined || o.gross_pay !== undefined);

          const statCols = [
            { key: 'requests', title: 'Requests', value: summary.total_requests ?? overtimes.length },
            { key: 'gross',    title: 'Gross Hours', value: summary.total_gross_hours ?? '—', suffix: 'hrs' },
            { key: 'net',      title: 'Net Hours', value: summary.total_net_hours ?? '—', suffix: 'hrs' },
            ...(canSeePay ? [{ key: 'pay', title: 'Total Pay', value: summary.total_pay ?? 0, prefix: '€', precision: 2 }] : []),
          ];
          const statSpan = Math.floor(24 / statCols.length);

          return (
            <Card title="Report Results" size="small">
              {subject && (
                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ margin: 0 }}>{subject}</Title>
                  {d.employee?.empEmail && <Text type="secondary">{d.employee.empEmail}</Text>}
                </div>
              )}

              <Row gutter={16} style={{ marginBottom: 20 }}>
                {statCols.map((s) => (
                  <Col span={statSpan} key={s.key}>
                    <Statistic title={s.title} value={s.value} suffix={s.suffix} prefix={s.prefix} precision={s.precision} />
                  </Col>
                ))}
              </Row>

              {overtimes.length === 0 ? (
                <Empty description="No overtime records for this selection" />
              ) : (
                <Table
                  rowKey="id"
                  dataSource={overtimes}
                  size="small"
                  pagination={false}
                  scroll={{ x: 900 }}
                  columns={[
                    { title: 'Request #', dataIndex: 'request_number', width: 130 },
                    { title: 'Festival',  dataIndex: 'festival_name', ellipsis: true },
                    { title: 'Date',      dataIndex: 'final_date', render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
                    { title: 'Day Type',  dataIndex: 'day_type', render: (v) => DAY_TYPE_LABELS[v] || fmt(v) },
                    { title: 'Net Hours', dataIndex: 'net_overtime_hours', render: fmtHrs },
                    { title: 'Rate',      dataIndex: 'rate_multiplier', render: (v) => (v != null ? `×${v}` : '—') },
                    ...(canSeePay ? [
                      { title: 'Hourly Rate', dataIndex: 'employee_hourly_rate', render: fmtEur },
                      { title: 'Gross Pay', dataIndex: 'gross_pay', render: fmtEur },
                    ] : []),
                    { title: 'Status',    dataIndex: 'status', render: statusTag },
                  ]}
                />
              )}
            </Card>
          );
        })()}
      </Space>
    </div>
  );

  const CoordinatorMgmtContent = (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={5} style={{ margin: 0 }}>Manager Management</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { addCoordForm.resetFields(); setAddCoordOpen(true); }}>
          Add Manager
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={coordinators}
        columns={[
          { title: 'Employee', dataIndex: 'name', ellipsis: true },
          { title: 'Festival', dataIndex: 'festival_name', ellipsis: true, render: (v) => v || 'All Festivals' },
          { title: 'Added',    dataIndex: 'created_at',   render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
          {
            title: 'Actions', key: 'actions',
            render: (_, rec) => (
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveCoord(rec.id)}>
                Remove
              </Button>
            ),
          },
        ]}
        size="small"
      />
    </div>
  );

  const PublicHolidaysContent = (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={5} style={{ margin: 0 }}>Public Holidays</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openPh()}>
          Add Holiday
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={publicHolidays}
        columns={[
          {
            title: 'Date', dataIndex: 'holiday_date', width: 120,
            render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
            sorter: (a, b) => (a.holiday_date || '').localeCompare(b.holiday_date || ''),
            defaultSortOrder: 'ascend',
          },
          { title: 'Name', dataIndex: 'holiday_name', ellipsis: true },
          {
            title: 'Actions', key: 'actions', width: 80,
            render: (_, rec) => (
              <Space>
                <Button size="small" icon={<EyeOutlined />} onClick={() => openPh(rec)} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handlePhDelete(rec.id)} />
              </Space>
            ),
          },
        ]}
        size="small"
        // A few years of holidays fit on one page; pages only appear past 100.
        pagination={{ pageSize: 100, hideOnSinglePage: true, showSizeChanger: false }}
      />
    </div>
  );


  const viewBody = {
    schedule: ScheduleView,
    requests: canSeeRequests ? RequestsView : ScheduleView,
    my:       MyView,
    coords:   isAdmin ? <section className="ovt-card ovt-card--pad">{CoordinatorMgmtContent}</section> : ScheduleView,
    holidays: isAdmin ? <section className="ovt-card ovt-card--pad">{PublicHolidaysContent}</section> : ScheduleView,
    reports:  canSeeReports ? <section className="ovt-card ovt-card--pad">{ReportsContent}</section> : ScheduleView,
  }[view] || ScheduleView;

  const rejectedCount = stepCounts.rejected || 0;
  const activeStep = view === 'requests' ? stepFilter : view === 'my' ? myFilter : null;

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */
  return (
    <DOTheme>
    <Layout className="do-shell do-scope ovt-page">
      <NavigationBar />
      <main className="do-main ovt-main">
        <aside className="ovt-rail">
          <div className="ovt-rail__head">
            <h1 className="ovt-title">Overtime</h1>
            {isFMorSO && (
              <Button type="primary" className="ovt-rail__new" icon={<PlusOutlined />}
                onClick={() => { createForm.resetFields(); setCreateOpen(true); }}>
                New request
              </Button>
            )}
          </div>

          <div className="ovt-rail__group">
            <p className="ovt-rail__label">Steps</p>
            {/* The four steps, with what each viewer can see in them */}
            <ol className="ovt-steps" aria-label="Overtime steps">
              {STEP_DEFS.map((s, i) => (
                <li key={s.key}>
                  <button
                    type="button"
                    className={`ovt-step tone-${s.tone}${youOn[s.key] ? ' is-you' : ''}${activeStep === s.key ? ' is-on' : ''}`}
                    aria-pressed={activeStep === s.key}
                    onClick={() => openStep(s.key)}
                  >
                    <IconTile tone={s.tone} icon={s.icon} />
                    <span className="ovt-step__text">
                      <span className="ovt-step__n">Step {i + 1}{youOn[s.key] ? ' · you' : ''}</span>
                      <span className="ovt-step__name">{s.name}</span>
                    </span>
                    <span className="ovt-step__count num">{stepCounts[s.key] || 0}</span>
                  </button>
                </li>
              ))}
              {rejectedCount > 0 && (
                <li className="ovt-steps__rejected">
                  <button
                    type="button"
                    className={`ovt-step ovt-step--rejected tone-bad${activeStep === 'rejected' ? ' is-on' : ''}`}
                    aria-pressed={activeStep === 'rejected'}
                    onClick={() => openStep('rejected')}
                  >
                    <IconTile tone="bad" icon={STEP_ICON.rejected} />
                    <span className="ovt-step__text">
                      <span className="ovt-step__n">Rejected</span>
                      <span className="ovt-step__name">{isFMorSO ? 'To amend' : 'Sent back'}</span>
                    </span>
                    <span className="ovt-step__count num">{rejectedCount}</span>
                  </button>
                </li>
              )}
            </ol>
          </div>

          <nav className="ovt-rail__group ovt-views" aria-label="Overtime views">
            <p className="ovt-rail__label">Views</p>
            {[...viewItems, ...adminItems].map((v, i) => (
              <React.Fragment key={v.key}>
                {i === viewItems.length && <span className="ovt-views__gap" aria-hidden="true" />}
                <button
                  type="button"
                  className={`ovt-view${i >= viewItems.length ? ' ovt-view--admin' : ''}${view === v.key ? ' is-on' : ''}`}
                  aria-current={view === v.key ? 'page' : undefined}
                  onClick={() => setView(v.key)}
                >
                  <span className="ovt-view__icon" aria-hidden="true">{v.icon}</span>
                  {v.label}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </aside>

        <div className="ovt-sheet">{viewBody}</div>
      </main>

      {/* ══ CREATE REQUEST MODAL ════════════════════════════════ */}
      <Modal
        title="Create Overtime Request"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={createLoading}
        width={580}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="employee_ids" label="Employee(s)" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Select one or more employees"
              options={employees.map(e => ({ value: e.id, label: e.name || e.full_name }))}
            />
          </Form.Item>
          <Form.Item name="festival_id" label="Festival" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select festival">
              {festivals.map(f => <Option key={f.id} value={f.id}>{f.name}</Option>)}
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="festival_date_from" label="Date From" rules={[{ required: true }]} style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="festival_date_to" label="Date To" rules={[{ required: true }]} style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="estimated_time_from" label="Time From" rules={[{ required: true }]} style={{ flex: 1 }}>
              <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="estimated_time_to" label="Time To" rules={[{ required: true }]} style={{ flex: 1 }}>
              <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item name="estimated_task" label="Task Description" rules={[{ required: true }]}>
            <TextArea rows={3} placeholder="Describe the task to be performed..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ══ COORDINATOR DECISION MODAL ══════════════════════════ */}
      <Modal
        title="Manager Decision"
        open={coordDecOpen}
        onCancel={() => setCoordDecOpen(false)}
        footer={null}
        destroyOnHidden
      >
        {coordDecRec && (
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Employee">{coordDecRec.employee_name}</Descriptions.Item>
            <Descriptions.Item label="Festival">{coordDecRec.festival_name}</Descriptions.Item>
            <Descriptions.Item label="Dates">
              {coordDecRec.festival_date_from ? dayjs(coordDecRec.festival_date_from).format('DD/MM/YYYY') : '—'}
              {' – '}
              {coordDecRec.festival_date_to   ? dayjs(coordDecRec.festival_date_to).format('DD/MM/YYYY')   : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Est. Hours">{fmtHrs(coordDecRec.estimated_hours)}</Descriptions.Item>
            <Descriptions.Item label="Task">{coordDecRec.estimated_task || '—'}</Descriptions.Item>
          </Descriptions>
        )}
        <Form form={coordDecForm} layout="vertical">
          <Form.Item name="notes" label="Notes / Reason (optional)">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
        {coordDecRec && (
          <Space>
            <Button type="primary" loading={coordDecLoading} icon={<CheckOutlined />}
              onClick={() => handleCoordDecision('approved')}>
              Approve
            </Button>
            <Button danger loading={coordDecLoading} icon={<CloseOutlined />}
              onClick={() => handleCoordDecision('rejected')}>
              Reject
            </Button>
          </Space>
        )}
      </Modal>

      {/* ══ FM FINAL APPROVAL MODAL ═════════════════════════════ */}
      <Modal
        title="FM / Senior Officer — Final Approval"
        open={fmApprovalOpen}
        onCancel={() => setFmApprovalOpen(false)}
        onOk={handleFmApproval}
        confirmLoading={fmApprovalLoading}
        width={560}
        destroyOnHidden
      >
        <Alert
          showIcon type="info" style={{ marginBottom: 16 }}
          message="Enter the actual hours worked after the festival has ended."
        />
        <Form form={fmApprovalForm} layout="vertical">
          <Form.Item name="final_date" label="Date of Work" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="final_time_in" label="Time In" rules={[{ required: true }]} style={{ flex: 1 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="final_time_out" label="Time Out" rules={[{ required: true }]} style={{ flex: 1 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item name="final_hours" label="Actual Hours Worked" rules={[{ required: true }]}>
            <InputNumber min={0.5} max={24} step={0.5} addonAfter="hrs" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="final_task" label="Actual Task Performed" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="notes" label="Notes (optional)">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ══ DETAIL VIEW MODAL ═══════════════════════════════════ */}
      <Modal
        title={`Overtime Request ${detailRec?.request_number ?? `#${detailRec?.id ?? ''}`}`}
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailRec(null); }}
        footer={null}
        width={700}
        destroyOnHidden
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : detailRec ? (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status" span={2}>{statusTag(detailRec.status)}</Descriptions.Item>
              <Descriptions.Item label="Employee">{fmt(detailRec.employee_name)}</Descriptions.Item>
              <Descriptions.Item label="Festival">{fmt(detailRec.festival_name)}</Descriptions.Item>
              <Descriptions.Item label="Date From">
                {detailRec.festival_date_from ? dayjs(detailRec.festival_date_from).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Date To">
                {detailRec.festival_date_to ? dayjs(detailRec.festival_date_to).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Est. Hours">{fmtHrs(detailRec.estimated_hours)}</Descriptions.Item>
              <Descriptions.Item label="Est. Time">
                {detailRec.estimated_time_from
                  ? `${detailRec.estimated_time_from.slice(0,5)} – ${(detailRec.estimated_time_to || '').slice(0,5)}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Est. Task" span={detailRec.estimated_task?.length > 40 ? 2 : 1}>
                {fmt(detailRec.estimated_task)}
              </Descriptions.Item>
            </Descriptions>

            {(detailRec.final_hours != null || detailRec.actual_start_time) && (
              <>
                <Divider orientation="left">Actual / Final</Divider>
                <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                  {detailRec.final_date     && <Descriptions.Item label="Final Date">{dayjs(detailRec.final_date).format('DD/MM/YYYY')}</Descriptions.Item>}
                  {detailRec.final_time_in  && <Descriptions.Item label="Time In">{detailRec.final_time_in}</Descriptions.Item>}
                  {detailRec.final_time_out && <Descriptions.Item label="Time Out">{detailRec.final_time_out}</Descriptions.Item>}
                  {detailRec.final_hours    != null && <Descriptions.Item label="Final Hours">{fmtHrs(detailRec.final_hours)}</Descriptions.Item>}
                  {detailRec.final_task     && <Descriptions.Item label="Final Task" span={2}>{detailRec.final_task}</Descriptions.Item>}
                  {detailRec.actual_start_time && <Descriptions.Item label="Employee Start">{dayjs(detailRec.actual_start_time).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                  {detailRec.actual_end_time   && <Descriptions.Item label="Employee End">{dayjs(detailRec.actual_end_time).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                </Descriptions>
              </>
            )}

            {detailRec.net_hours != null && (
              <>
                <Divider orientation="left">Pay Calculation</Divider>
                <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Day Type">
                    {DAY_TYPE_LABELS[detailRec.day_type] || fmt(detailRec.day_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rate Multiplier">{fmt(detailRec.rate_multiplier)}×</Descriptions.Item>
                  <Descriptions.Item label="Break Deduction">
                    {detailRec.break_hours ? fmtHrs(detailRec.break_hours) : 'None'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Net Hours">{fmtHrs(detailRec.net_hours)}</Descriptions.Item>
                  {detailRec.hourly_rate !== undefined && (
                    <Descriptions.Item label="Hourly Rate">{fmtEur(detailRec.hourly_rate)}</Descriptions.Item>
                  )}
                  {detailRec.gross_pay !== undefined && (
                    <Descriptions.Item label="Gross Pay">
                      <Text strong>{fmtEur(detailRec.gross_pay)}</Text>
                    </Descriptions.Item>
                  )}
                  {detailRec.disturbance_hours != null && (
                    <Descriptions.Item label="Disturbance Allowance">
                      {fmtHrs(detailRec.disturbance_hours)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {Array.isArray(detailRec.history) && detailRec.history.length > 0 && (
              <>
                <Divider orientation="left">History</Divider>
                <Timeline
                  items={detailRec.history.map(h => ({
                    color: h.action?.includes('reject') ? 'red'
                         : (h.action?.includes('approv') || h.action?.includes('complet')) ? 'green'
                         : 'blue',
                    children: (
                      <div>
                        <Text strong>{(h.action || '').replace(/_/g, ' ')}</Text>
                        {h.notes && <div><Text type="secondary">{h.notes}</Text></div>}
                        <div>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {h.actor_name || h.actor}
                            {h.created_at ? ` — ${dayjs(h.created_at).format('DD/MM/YYYY HH:mm')}` : ''}
                          </Text>
                        </div>
                      </div>
                    ),
                  }))}
                />
              </>
            )}
          </div>
        ) : null}
      </Modal>

      {/* ══ ADD COORDINATOR MODAL (admin) ═══════════════════════ */}
      <Modal
        title="Add Manager"
        open={addCoordOpen}
        onCancel={() => setAddCoordOpen(false)}
        onOk={handleAddCoord}
        confirmLoading={addCoordLoading}
        destroyOnHidden
      >
        <Form form={addCoordForm} layout="vertical">
          <Form.Item name="employee_ids" label="Employee(s)" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Select one or more employees"
              options={employees.map(e => ({ value: e.id, label: e.name || e.full_name }))}
            />
          </Form.Item>
          <Form.Item name="festival_id" label="Festival (optional — leave blank for all)">
            <Select showSearch optionFilterProp="children" placeholder="All festivals" allowClear>
              {festivals.map(f => <Option key={f.id} value={f.id}>{f.name}</Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ══ AMEND REQUEST MODAL (FM/SO for rejected) ═══════════ */}
      <Modal
        title={`Amend Request ${amendRec?.request_number ?? ''}`}
        open={amendOpen}
        onCancel={() => setAmendOpen(false)}
        onOk={handleAmend}
        confirmLoading={amendLoading}
        width={600}
        destroyOnHidden
      >
        {amendLoading && !amendRec ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <Form form={amendForm} layout="vertical">
            <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select employee"
                options={employees.map(e => ({ value: e.id, label: e.name || e.full_name }))}
              />
            </Form.Item>
            <Form.Item name="festival_id" label="Festival" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="children" placeholder="Select festival">
                {festivals.map(f => <Option key={f.id} value={f.id}>{f.name}</Option>)}
              </Select>
            </Form.Item>
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Item name="festival_date_from" label="Date From" rules={[{ required: true }]} style={{ flex: 1 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item name="festival_date_to" label="Date To" rules={[{ required: true }]} style={{ flex: 1 }}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Item name="estimated_time_from" label="Time From" rules={[{ required: true }]} style={{ flex: 1 }}>
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="estimated_time_to" label="Time To" rules={[{ required: true }]} style={{ flex: 1 }}>
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item name="estimated_task" label="Task Description" rules={[{ required: true }]}>
              <TextArea rows={3} placeholder="Describe the task to be performed..." />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* ══ PUBLIC HOLIDAY MODAL (admin) ════════════════════════ */}
      <Modal
        title={phRec ? 'Edit Public Holiday' : 'Add Public Holiday'}
        open={phOpen}
        onCancel={() => setPhOpen(false)}
        onOk={handlePhSave}
        confirmLoading={phLoading}
        destroyOnHidden
      >
        <Form form={phForm} layout="vertical">
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="name" label="Holiday Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Independence Day" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
    </DOTheme>
  );
};

export default Overtime;
